import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useSizingImpact from "@babackapp/private-api-client/client/use-sizing-impact";
import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@babackapp/ui/components/chart";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, Info } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { MOCK_IMPACT, USE_MOCK_DATA } from "../lib/mock-data";

type SizingImpactSectionProps = {
  fromTimestamp?: number;
  toTimestamp?: number;
};

function KpiCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: number | null;
  variant?: "positive" | "negative" | "neutral";
}) {
  const formatted = value != null ? `${(value * 100).toFixed(1)}%` : "—";

  return (
    <Card className="flex-1">
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-2xl font-bold tabular-nums">{formatted}</span>
          {variant === "positive" && value != null && (
            <Badge variant="success" className="gap-1">
              <ArrowDown className="h-3 w-3" />
              {Math.abs(value * 100).toFixed(1)}%
            </Badge>
          )}
          {variant === "negative" && value != null && (
            <Badge variant="destructive" className="gap-1">
              <ArrowUp className="h-3 w-3" />
              {Math.abs(value * 100).toFixed(1)}%
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function SizingImpactSection({
  fromTimestamp,
  toTimestamp,
}: SizingImpactSectionProps) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const chartConfig = {
    sizingRate: {
      label: t("sizing.impact.sizingRate"),
      color: "hsl(var(--chart-1))",
    },
  } satisfies ChartConfig;

  const apiResult = useSizingImpact({
    shopId: shop.id,
    fromTimestamp,
    toTimestamp,
  });

  const impact = USE_MOCK_DATA ? MOCK_IMPACT : apiResult.data;
  const isLoading = USE_MOCK_DATA ? false : apiResult.isLoading;

  if (isLoading) {
    return (
      <Card className="col-span-4">
        <CardContent className="flex items-center justify-center h-48">
          <div className="animate-pulse text-muted-foreground">
            {t("misc.loading")}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state: widget not activated
  if (!impact?.widgetActivatedAt) {
    return (
      <Card className="col-span-4">
        <CardContent className="flex flex-col items-center justify-center h-48 gap-2">
          <Info className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("sizing.impact.emptyNoWidget")}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Empty state: not enough data
  if (!impact.timeSeries || impact.timeSeries.length === 0) {
    return (
      <Card className="col-span-4">
        <CardContent className="flex flex-col items-center justify-center h-48 gap-2">
          <Info className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("sizing.impact.emptyNoData")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const variationVariant =
    impact.variation != null
      ? impact.variation < 0
        ? "positive"
        : impact.variation > 0
          ? "negative"
          : "neutral"
      : undefined;

  return (
    <>
      {/* KPI Cards */}
      <div className="col-span-4 flex gap-4">
        <KpiCard
          label={t("sizing.impact.beforeRate")}
          value={impact.beforeRate}
        />
        <KpiCard
          label={t("sizing.impact.afterRate")}
          value={impact.afterRate}
        />
        <KpiCard
          label={t("sizing.impact.variation")}
          value={impact.variation}
          variant={variationVariant}
        />
      </div>

      {/* Chart */}
      <Card className="col-span-4">
        <CardHeader>
          <CardTitle className="text-md">{t("sizing.impact.title")}</CardTitle>
        </CardHeader>
        <CardContent className="h-[250px]">
          <ChartContainer config={chartConfig} className="h-full w-full">
            <AreaChart data={impact.timeSeries} margin={{ left: 0, right: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                type="number"
                scale="time"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value: number) =>
                  value ? format(new Date(value), "d MMM") : ""
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                width={50}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => {
                      if (payload?.[0]?.payload?.date) {
                        return format(
                          new Date(payload[0].payload.date),
                          "d MMM yyyy",
                        );
                      }
                      return "";
                    }}
                    formatter={(value) => {
                      const numVal = Number(value);
                      return `${(numVal * 100).toFixed(1)}%`;
                    }}
                  />
                }
              />
              {impact.widgetActivatedAt && (
                <ReferenceLine
                  x={impact.widgetActivatedAt}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  label={{
                    value: t("sizing.impact.widgetActivated"),
                    position: "top",
                    fill: "hsl(var(--primary))",
                    fontSize: 12,
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="sizingRate"
                stroke="hsl(var(--chart-1))"
                strokeWidth={2}
                fill="hsl(var(--chart-1))"
                fillOpacity={0.15}
                isAnimationActive={false}
              />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </>
  );
}
